import { lastValueFrom, Observable } from 'rxjs';
import { Injectable, Injector } from '@angular/core';
import { HttpDataService } from 'core/services/http-data.service';
import { LocalesService } from 'core/services/locales.service';
import { Subscription } from '../models/subscription';

@Injectable()
export class SubscriptionsService extends HttpDataService {
  public list: Subscription[];

  constructor(protected injector: Injector, public locales: LocalesService) {
    super(injector, '/subscriptions');
  }

  renewSubscription(subscriptionToRenewId: string, data: any): Promise<any> {
    const url = `${this.baseUrl}/${subscriptionToRenewId}/renewal`;
    return this.putRequest(url, data, this.getRequestOptions());
  }

  public async getClientCreditsResume(clientId: string): Promise<any> {
    const url = `${this.baseUrl}/${clientId}/resume`;
    return lastValueFrom(this.fetchRequest(url, this.getRequestOptions(), null));
  }

  public async checkCreditsAvailability(clientId: string, credits: number): Promise<any> {
    return lastValueFrom(this.http.get(`${this.baseUrl}/${clientId}/availability/${credits}`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  public async getClientSubscriptionExpiryDate(clientId: string): Promise<any> {
    return lastValueFrom(this.http.get(`${this.baseUrl}/${clientId}/expiryDate`))
      .then((res) => res)
      .catch((error) => this.handlePromiseError(error));
  }

  public async getClientSubscription(clientId: string, deleted?: boolean): Promise<any> {
    let url = `${this.baseUrl}/?clientId=${clientId}&type=users&sort=-endDate`;
    if (deleted !== undefined)
      url = `${this.baseUrl}/?clientId=${clientId}&type=users&sort=-endDate&deleted=${deleted}`;
    return lastValueFrom(this.fetchRequest(url, { limit: 2 }, null)).then((subscriptions) => {
      let latestSubscription = subscriptions && subscriptions.docs.length ? subscriptions.docs[0] : null;

      if (!latestSubscription) {
        return null;
      }
      if (!this.hasStarted(latestSubscription) && subscriptions.docs.length > 1) {
        latestSubscription = subscriptions.docs[1];
      }
      return latestSubscription;
    });
  }

  private hasStarted(subscription: Subscription): boolean {
    const start = new Date(subscription.startDate).toISOString();
    const now = new Date().toISOString();

    return start < now;
  }

  public downloadSubscriptionReport(subscriptionId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${subscriptionId}/report`, {
      responseType: 'blob'
    });
  }

  public downloadSeatsReport(clientId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/consumptions/users/report?clientId=${clientId}`, {
      responseType: 'blob'
    });
  }

  public downloadClientCreditsReport(clientId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/client/${clientId}/credits-report`, {
      responseType: 'blob'
    });
  }
}
